import { RightOutlined } from "@ant-design/icons"
import { Card, Flex, Space, Tag, Typography } from "antd"
import React, { type ComponentType } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { cn } from "../../../../utils/cn.ts"

export type TemplateAccent = "blue" | "green" | "purple" | "sky"

const ACCENT: Record<TemplateAccent, { wrapper: string; icon: string }> = {
  blue: { wrapper: "bg-blue-50", icon: "text-blue-500" },
  green: { wrapper: "bg-green-50", icon: "text-green-500" },
  purple: { wrapper: "bg-purple-50", icon: "text-purple-500" },
  sky: { wrapper: "bg-sky-50", icon: "text-sky-500" }
}

interface TemplateTypeCardProps {
  title: string
  description: string
  to: string
  count: number
  /** What the count is of; the default counts templates. */
  countKey?: string
  Icon: ComponentType<{ className?: string }>
  accent: TemplateAccent
  chips?: string[]
}

export function TemplateTypeCard({
  title,
  description,
  to,
  count,
  countKey = "settingsTemplatesHub.card.count",
  Icon,
  accent,
  chips
}: TemplateTypeCardProps) {
  const { t } = useTranslation()

  return (
    <Link
      to={to}
      className={
        "block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      }
    >
      <Card
        className={"h-full rounded-lg transition-shadow hover:shadow-md"}
        classNames={{ body: "flex h-full flex-col gap-3 p-4" }}
      >
        <Flex align={"flex-start"} gap={"middle"}>
          <div
            aria-hidden
            className={cn(
              "flex size-12 shrink-0 items-center justify-center rounded-lg",
              ACCENT[accent].wrapper
            )}
          >
            <Icon className={cn("text-2xl", ACCENT[accent].icon)} />
          </div>
          <Flex vertical gap={4}>
            <Typography.Title className={"mb-0 font-semibold"} level={5}>
              {title}
            </Typography.Title>
            <Typography.Text type={"secondary"}>{description}</Typography.Text>
            {!!chips?.length && (
              <Space className={"mt-1"} size={8} wrap>
                {chips.map((chip) => (
                  <Tag className={"m-0"} key={chip}>
                    {chip}
                  </Tag>
                ))}
              </Space>
            )}
          </Flex>
        </Flex>
        <Flex
          align={"center"}
          className={"mt-auto pt-3"}
          justify={"space-between"}
        >
          <Typography.Text type={"secondary"}>
            {t(countKey, { count })}
          </Typography.Text>
          <RightOutlined aria-hidden className={"text-slate-400"} />
        </Flex>
      </Card>
    </Link>
  )
}
